import { useEffect, useState } from 'react';
import { placeLabel } from '../../../utils/formatters';
import { behaviorTypeOptions } from './constants';
import CategoriesTab from './tabs/CategoriesTab';
import ContainerTab from './tabs/ContainerTab';
import GeneralTab from './tabs/GeneralTab';
import HoursTab from './tabs/HoursTab';
import LocationTab from './tabs/LocationTab';
import SettingsTab from './tabs/SettingsTab';
import { Button, Typography, Stack, TextField, Icon, IconButton } from '@mui/material';
import { Close } from '@mui/icons-material';
const PlaceDialog = ({
  open,
  selectedPlace,
  placeForm,
  setPlaceForm,
  updateNested,
  addRange,
  removeRange,
  updateRange,
  duplicateDay,
  duplicateTargets,
  setDuplicateTargets,
  setOpen247,
  isOpen247,
  onClose,
  onSave,
  onLoadNearbyFootprints,
  onLoadContainerManagement,
  onUpdateContainerFloors,
  onManageContainerFloorUnit,
  onAssignPlaceToContainer,
  onRemovePlaceFromContainer,
  onSavePlaceFootprint,
  onDelete,
  loading,
  behaviorTypes = behaviorTypeOptions,
  categories = [],
}) => {
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [nearbyFootprints, setNearbyFootprints] = useState([]);
  const [nearbyFootprintsLoading, setNearbyFootprintsLoading] = useState(false);
  const [footprintCollisions, setFootprintCollisions] = useState([]);
  const [activeTab, setActiveTab] = useState('general');
  const [categorySearch, setCategorySearch] = useState('');
  useEffect(() => {
    setDeleteConfirmation('');
    setShowDeleteConfirmation(false);
    setDeleteError('');
    setActiveTab('general');
  }, [selectedPlace?.placeId, open]);
  useEffect(() => {
    if (activeTab === 'container' && placeForm.placeKind !== 'container') setActiveTab('general');
  }, [activeTab, placeForm.placeKind]);
  useEffect(() => {
    let active = true;
    setNearbyFootprints([]);
    setFootprintCollisions([]);
    if (!open || !selectedPlace?.placeId)
      return () => {
        active = false;
      };
    setNearbyFootprintsLoading(true);
    onLoadNearbyFootprints(selectedPlace.placeId)
      .then((items) => {
        if (active) setNearbyFootprints(Array.isArray(items) ? items : []);
      })
      .catch(() => {
        if (active) setNearbyFootprints([]);
      })
      .finally(() => {
        if (active) setNearbyFootprintsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [onLoadNearbyFootprints, open, selectedPlace?.placeId]);
  if (!open || !selectedPlace) return null;
  const confirmationName = selectedPlace.name?.he || selectedPlace.name?.en || '';
  const canDelete = deleteConfirmation.trim() === confirmationName.trim();
  const confirmDelete = async () => {
    if (!canDelete || loading) return;
    setDeleteError('');
    try {
      await onDelete(deleteConfirmation);
    } catch (error) {
      setDeleteError(
        error.message === 'PLACE_HAS_CONTAINED_PLACES'
          ? 'לא ניתן למחוק מתחם שיש בו מקומות פעילים.'
          : 'המחיקה נכשלה. המקום לא נמחק.',
      );
    }
  };
  return (
    <Stack
      onMouseDown={onClose}
      sx={{
        position: 'fixed',
        inset: '0',
        zIndex: '30',
        display: 'grid',
        placeItems: 'center',
        padding: '24px',
        background: 'rgba(21, 33, 31, .56)',
        backdropFilter: 'blur(6px)',
      }}
    >
      <Stack
        onSubmit={(event) => {
          if (footprintCollisions.length) {
            event.preventDefault();
            return;
          }
          onSave(event);
        }}
        onMouseDown={(event) => event.stopPropagation()}
        dir="rtl"
        sx={(theme) => ({
          width: '100%',
          height: '100%',
          display: 'grid',
          gridTemplateRows: 'auto auto minmax(0, 1fr) auto',
          contain: 'layout',
          background: 'var(--panel)',
          borderRadius: '20px',
          boxShadow: 'var(--shadow-lg)',
          overflow: 'hidden',
          textAlign: 'right',
          [theme.breakpoints.down('sm')]: {
            width: '100%',
            height: '100vh',
            maxHeight: '100vh',
            minHeight: '100vh',
            borderRadius: '0',
          },
          border: '1px solid rgba(255,255,255,.45)',
        })}
      >
        <Stack
          direction="row"
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: 2,
            borderBottom: '1px solid var(--line)',
          }}
        >
          <Stack>
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: '700',
              }}
            >
              {placeLabel(selectedPlace)}
            </Typography>
            <Typography
              sx={{
                fontSize: '14px',
                color: 'var(--text-secondary)',
              }}
            >
              {selectedPlace.placeId}
            </Typography>
          </Stack>

          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Stack>

        <Stack
          direction="row"
          aria-label="עריכת מקום"
          sx={{
            display: 'flex',
            gap: '6px',
            padding: '10px 22px 0',
            overflowX: 'auto',
            borderBottom: '1px solid var(--line)',
            background: '#f8fafc',
          }}
        >
          {[
            ['general', 'פרטים כלליים'],
            ['settings', 'הגדרות'],
            ['categories', `קטגוריות (${placeForm.categories.length})`],
            ['location', 'מיקום וגבולות המקום'],
            ['hours', 'שעות פעילות'],
            ...(placeForm.placeKind === 'container' ? [['container', 'מתחם']] : []),
          ].map(([key, label]) => (
            <Button
              type="button"
              key={key}
              onClick={() => setActiveTab(key)}
              sx={String(activeTab === key ? 'active' : '')
                .split(/\s+/)
                .map(
                  (name) =>
                    ({
                      active: {
                        background: '#1570ef',
                        borderColor: '#1570ef',
                        '& small': {
                          color: '#ddebff',
                        },
                        color: '#fff',
                        boxShadow: '0 4px 10px rgba(21, 112, 239, .25)',
                        borderBottomColor: 'var(--primary)',
                        transform: 'none',
                      },
                    })[name],
                )
                .filter(Boolean)}
            >
              {label}
            </Button>
          ))}
        </Stack>

        <Stack direction="row" sx={{ p: 2, mb: 2, overflow: "scroll" }}>

          {activeTab === 'general' && (
            <GeneralTab
              placeForm={placeForm}
              setPlaceForm={setPlaceForm}
              updateNested={updateNested}
              behaviorTypes={behaviorTypes}
            />
          )}

          {activeTab === 'settings' && (
            <SettingsTab placeForm={placeForm} setPlaceForm={setPlaceForm} />
          )}

          {activeTab === 'categories' && (
            <CategoriesTab
              categories={categories}
              categorySearch={categorySearch}
              setCategorySearch={setCategorySearch}
              placeForm={placeForm}
              setPlaceForm={setPlaceForm}
            />
          )}

          {activeTab === 'location' && (
            <LocationTab
              placeForm={placeForm}
              setPlaceForm={setPlaceForm}
              nearbyFootprints={nearbyFootprints}
              nearbyFootprintsLoading={nearbyFootprintsLoading}
              setFootprintCollisions={setFootprintCollisions}
            />
          )}

          {activeTab === 'hours' && (
            <HoursTab
              days={placeForm.openingHours}
              addRange={addRange}
              removeRange={removeRange}
              updateRange={updateRange}
              duplicateDay={duplicateDay}
              duplicateTargets={duplicateTargets}
              setDuplicateTargets={setDuplicateTargets}
              setOpen247={setOpen247}
              isOpen247={isOpen247}
            />
          )}

          {activeTab === 'container' && placeForm.placeKind === 'container' && (
            <ContainerTab
              key={selectedPlace.placeId}
              placeId={selectedPlace.placeId}
              setPlaceForm={setPlaceForm}
              onLoad={onLoadContainerManagement}
              onUpdateFloors={onUpdateContainerFloors}
              onManageFloorUnit={onManageContainerFloorUnit}
              onAssignPlace={onAssignPlaceToContainer}
              onRemovePlace={onRemovePlaceFromContainer}
              onSavePlaceFootprint={onSavePlaceFootprint}
            />
          )}

        </Stack>

        <Stack
          direction="row"
          sx={(theme) => ({
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: '16px',
            padding: '20px 24px',
            borderBottom: '0',
            borderTop: '1px solid var(--line)',
            [theme.breakpoints.down('sm')]: {
              padding: '16px',
              position: 'sticky',
              bottom: '0',
              flexWrap: 'wrap',
              background: '#fff',
              '& button': {
                flex: '1 1 120px',
              },
            },
          })}
        >
          <Button
            type="button"
            disabled={loading}
            onClick={() => setShowDeleteConfirmation(true)}
            sx={{
              borderRightColor: '#d92d20',
              background: '#fee4e2',
              color: 'var(--danger)',
              '&:disabled': {
                opacity: '.45',
                cursor: 'not-allowed',
                transform: 'none',
              },
            }}
          >
            מחיקת מקום
          </Button>
          <Typography
            sx={{
              flex: '1',
            }}
          />
          <Button
            type="button"
            onClick={onClose}
            sx={{
              background: '#edf2f7',
              color: 'var(--text)',
              '&:hover': {
                background: '#ddebff',
                color: '#1269e8',
              },
            }}
          >
            ביטול
          </Button>
          <Button
            onClick={onSave}
            disabled={
              loading ||
              footprintCollisions.length > 0 ||
              (placeForm.footprintParts || [placeForm.footprintPoints]).some(
                (part) => part.length > 0 && part.length < 3,
              )
            }
          >
            שמירה
          </Button>
        </Stack>

        {showDeleteConfirmation && (
          <Stack
            role="presentation"
            onMouseDown={() => setShowDeleteConfirmation(false)}
            sx={{
              position: 'fixed',
              inset: '0',
              zIndex: '5',
              display: 'grid',
              placeItems: 'stretch',
              padding: '0',
              background: 'rgba(15, 23, 42, 0.54)',
            }}
          >
            <Stack
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="delete-place-title"
              onMouseDown={(event) => event.stopPropagation()}
              sx={{
                width: 'min(440px, 100%)',
                display: 'grid',
                gap: '16px',
                padding: '24px',
                border: '1px solid #fecdca',
                borderRadius: '12px',
                background: 'var(--panel)',
                boxShadow: '0 24px 70px rgba(15, 23, 42, 0.3)',
                '& p': {
                  margin: '0',
                },
              }}
            >
              <Typography id="delete-place-title">מחיקת המקום</Typography>
              <Typography>
                הפעולה תסתיר את המקום מהפאנל ומהאפליקציה. כדי לאשר, יש להקליד בדיוק:
              </Typography>
              <Typography
                sx={{
                  padding: '10px 12px',
                  borderRadius: '8px',
                  background: '#fef3f2',
                  color: 'var(--danger)',
                  overflowWrap: 'anywhere',
                }}
              >
                {confirmationName}
              </Typography>
              <Stack>
                שם המקום
                <TextField
                  autoFocus
                  value={deleteConfirmation}
                  onChange={(event) => setDeleteConfirmation(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter') return;
                    event.preventDefault();
                    confirmDelete();
                  }}
                  fullWidth
                />
              </Stack>
              {deleteError && (
                <Typography
                  sx={{
                    padding: '10px 12px',
                    borderRadius: '8px',
                    background: '#fef3f2',
                    color: 'var(--danger)',
                    fontWeight: '700',
                  }}
                >
                  {deleteError}
                </Typography>
              )}
              <Stack
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  gap: '10px',
                }}
              >
                <Button
                  type="button"
                  disabled={loading}
                  onClick={() => setShowDeleteConfirmation(false)}
                  sx={{
                    background: '#edf2f7',
                    color: 'var(--text)',
                    '&:hover': {
                      background: '#ddebff',
                      color: '#1269e8',
                    },
                  }}
                >
                  חזרה
                </Button>
                <Button
                  type="button"
                  disabled={!canDelete || loading}
                  onClick={confirmDelete}
                  sx={{
                    borderRightColor: '#d92d20',
                    background: '#fee4e2',
                    color: 'var(--danger)',
                    '&:disabled': {
                      opacity: '.45',
                      cursor: 'not-allowed',
                      transform: 'none',
                    },
                  }}
                >
                  מחיקה סופית
                </Button>
              </Stack>
            </Stack>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};
export default PlaceDialog;
